import { Component, OnInit, Input, OnChanges, SimpleChanges, SimpleChange, HostBinding, HostListener, ElementRef, ChangeDetectorRef, EventEmitter, Output } from '@angular/core';

export interface ColumnConfig {
  label: string,
  property: string | string[],
  actions?: ({
    type: 'icon', icon: string, class: string
  }|{
    type: 'label', label: string, class: string
  })[],
  isLoading?: boolean,
  filter?: boolean,
  width?: string,
  hidden?: boolean
}

type PaginationElement = {action: string | number, enabled: boolean, content: {type: 'label', label: string} | { type: 'icon', icon: string}};

@Component({
  selector: 'app-filterable-paginated-table',
  templateUrl: './filterable-paginated-table.component.html',
  styleUrls: ['./filterable-paginated-table.component.scss']
})
export class FilterablePaginatedTableComponent implements OnInit, OnChanges {

  @Input() width: number | string;
  @Input() columns: ColumnConfig[] = [];
  @Input() data: any[] = [];
  @Input() pageSize: number = 10;
  @Input() page: number = 1;
  @Input() paginationButtonsAllowed: number = 10;
  @Input() loading = false;
  @Output() actionClick = new EventEmitter<[any, number]>();

  public filteredRows: any[] = [];
  public currentPageId: number;
  public pageCount = 0;
  public maxPaginationButtons = 10;
  public filters: {property: string[], value: string, open: boolean}[] = [];
  public paginationElements: PaginationElement[] = [];
  public paginationRowRange: [number, number] = [0, 10];
  public visibleRows: any[] = [];
  public visibleColumns: ColumnConfig[];

  @HostBinding('style.width') public widthStyle: string = '';

  constructor(
    private elRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    const page: SimpleChange = changes.page;
    if (page) {
      const pageNum = page.currentValue;
      const isValidNumber = typeof pageNum === 'number' && !isNaN(pageNum) && pageNum > 0;
      if (!isValidNumber || (pageNum !== 0 && pageNum-1 > this.pageCount)) {
        console.warn('Skipped invalid page change');
      } else {
        this.currentPageId = pageNum - 1;
      }
    }
    const widthChange: SimpleChange = changes.width;
    if (widthChange) {
      if (typeof widthChange.currentValue === 'string') {
        this.widthStyle = widthChange.currentValue;
      } else {
        this.widthStyle = widthChange.currentValue + 'px';
      }
    }
    const pba = changes.paginationButtonsAllowed;
    if (pba) {
      const paginationButtonAmount = typeof pba.currentValue === 'string' ? parseInt(pba.currentValue, 10) : pba.currentValue;
      if (!isNaN(paginationButtonAmount) && (paginationButtonAmount == 0 || paginationButtonAmount >= 3)) {
        if (paginationButtonAmount % 2 !== 1) {
          console.warn('Avoid using even amounts of pagination buttons allowed because of simmetry of buttons');
        }
        this.maxPaginationButtons = paginationButtonAmount;
      } else {
        console.warn(`Skipped invalid paginationButtonsAllowed (${paginationButtonAmount})`);
      }
    }
    const data: SimpleChange = changes.data;
    if (data || page || pba || changes.loading || changes.pageSize) {
      this.updateContent();
    }
  }
  // Detect FocusIn and FocusOut via global click to blur and focus filter inputs
  @HostListener('window:click', ['$event'])
  onWindowClick(event) {
    let clickedButton: HTMLButtonElement;
    let clickedId: number;
    for (const node of event.composedPath()) {
      if (!node || !node.classList) {
        continue;
      }
      if (node.classList.contains('filter-input-wrapper')) {
        if (!node.hasAttribute('data-index')) {
          console.warn(`Filter input wrapper is missing data-index`);
          continue;
        }
        clickedId = parseInt(node.getAttribute('data-index'), 10);
      } else if (node.classList.contains('btn-filter')) {
        const button = node;
        if (!node.hasAttribute('data-index')) {
          console.warn(`Filter button wrapper is missing data-index`);
          continue;
        }
        clickedButton = button;
        clickedId = parseInt(node.getAttribute('data-index'), 10);
      }
    }

    this.openFilterByIndex(clickedId);
  }

  onActionButtonClick(row: any, buttonIndex: number) {
    this.actionClick.emit([row, buttonIndex]);
  }

  openFilterByIndex(index: number) {
    let unopenedFilter;
    if (index !== undefined && index !== null) {
      unopenedFilter = this.filters[index];
      if (!unopenedFilter) {
        console.warn(`Could not find filter ${index} to enable search input`);
        return;
      }
    }
    this.filters = this.filters.map((filter) => ({
      open: filter === unopenedFilter,
      property: filter.property,
      value: filter.value
    }));
    if (unopenedFilter) {
      setTimeout(() => {
        if (!this.elRef || !this.elRef.nativeElement) {
          return;
        }
        const input = this.elRef.nativeElement.querySelector(".filter-table-header input");
        if (input) {
          input.focus();
        }
      }, 100);
    }
    // this.changeDetectorRef.detectChanges();
  }

  isIncludedInFilters(row: any) {
    if (typeof row !== 'object' || !row) {
      return false;
    }
    for (const filter of this.filters) {
      if (!filter || !filter.value || !filter.property || !filter.property.length) {
        continue;
      }
      if (typeof row[filter.property[0]] === "undefined") {
        console.warn(`Could not filter by property "${filter.property.join(".")}" because it doesn't exist at row`);
        continue;
      }
      if (filter.property.length === 0) {
        continue;
      }
      const propValue = this.getValueFromRowAndPropertyArray(row, filter.property);
      if (propValue === null || propValue === undefined) {
        continue;
      }

      if (typeof propValue === 'string' || typeof propValue === 'number') {
        if (!propValue.toString().toLowerCase().includes(filter.value.toLowerCase())) {
          return false;
        }
      } else {
        return false;
      }
    }
    return true;
  }

  onFilterChange(columnConfig: ColumnConfig, index: number, event: Event) {
    if (typeof index !== "number" || !this.filters[index]) {
      console.warn(`Could not find correspondent filter index ${index}`);
      return;
    }
    if (!(event.target instanceof HTMLInputElement) || event.target.tagName !== "INPUT") {
      console.warn("Filter changed but event target was not the input");
      return;
    }
    if (this.filters[index].value !== event.target.value) {
      this.filters[index].value = event.target.value;
      this.filteredRows = this.data.filter(this.isIncludedInFilters.bind(this));
      this.updatePagination();
    }
  }

  onClearFilterClick(columnConfig: ColumnConfig, index: number) {
    if (typeof index !== "number" || !this.filters[index]) {
      console.warn(`Could not find correspondent filter index ${index}`);
    } else {
      this.filters[index].value = '';
      this.filteredRows = this.data.filter(this.isIncludedInFilters.bind(this));
      this.updatePagination();
    }
    if (!(event.target instanceof HTMLInputElement) || event.target.tagName !== "INPUT") {
      console.warn('Filter changed but event target was not the input');
    } else {
      event.target.value = '';
    }
  }

  updateContent() {
    // console.log('Updating content');
    const visibleColumns = this.columns.filter(c => !c.hidden);
    if (this.filters.length !== visibleColumns.length) {
      while (this.filters.length > visibleColumns.length) {
        this.filters.pop();
      }
      while (this.filters.length < visibleColumns.length) {
        const property = visibleColumns[this.filters.length].property;
        this.filters.push({ property: typeof property === 'string' ? [property] : property, value: '', open: false });
      }
    }
    this.visibleColumns = visibleColumns;
    const data = typeof this.data === 'undefined' ? [] : this.data;
    if (typeof data !== 'object') {
      throw new Error(`Invalid data into filterable paginated table type: expected "object", got "${typeof data}"`);
    }
    if (!(data instanceof Array)) {
      throw new Error('Invalid data into filterable paginated table prototype: expected array');
    }
    this.filteredRows = data.filter(this.isIncludedInFilters.bind(this));
    if (this.loading === false) {
      // console.log('updating pagination');
      this.updatePagination();
    } else {
      // console.log('Not updating content because of loading');
    }
  }

  onPageClick(element: PaginationElement, index: number) {
    if (!element || !element.enabled || element.action === 'active' || element.action === 'none') {
      return;
    }
    if (typeof element.action !== 'number') {
      console.warn(`Unknown element action of type "${typeof element.action}": "${element.action.toString()}"`);
      return;
    }
    this.setPageIdAfterClick(element.action);
  }

  onPreviousPageClick() {
    if (this.currentPageId-1 >= 0) {
      this.setPageIdAfterClick(this.currentPageId - 1);
    }
  }

  onNextPageClick() {
    if (this.currentPageId+1 < this.pageCount) {
      this.setPageIdAfterClick(this.currentPageId + 1);
    }
  }

  private setPageIdAfterClick(pageId: number) {
    this.currentPageId = pageId;
    this.updatePagination();
  }

  private getPaginationDescriptor(currentPageId: number, rows: number, pageSize: number, maxPageAmount: number) {
    const pageCount = Math.ceil(rows / pageSize);
    const elements: (string|number)[] = [];

    let realPageId;
    if (!currentPageId || currentPageId < 1) {
      realPageId = 0;
    } else if (currentPageId >= pageCount) {
      realPageId = pageCount-1;
    } else {
      realPageId = currentPageId;
    }

    if (maxPageAmount !== 0 && pageCount > 1) {
      elements.push(realPageId);
      let leftestPageId = realPageId;
      let rightestPageId = realPageId;
      let maxLoopIterations = pageCount <= maxPageAmount ? maxPageAmount : maxPageAmount - 2;
      for (let i = 1; i < maxLoopIterations; i++) {
        const hasPagesBefore = leftestPageId > 0;
        const hasPagesAfter = rightestPageId < pageCount;
        if (!hasPagesAfter || (hasPagesBefore && i % 2 === 1)) {
          leftestPageId--;
          if (hasPagesBefore) {
            // console.log(i, "add left", leftestPageId);
            elements.unshift(leftestPageId);
          }
        } else {
          if (rows / pageSize === i && i === maxLoopIterations - 1) {
            rightestPageId++;
            continue;
          }
          // console.log({rows, pageSize, i, maxLoopIterations});
          rightestPageId++;
          // console.log(i, "add right", rightestPageId, rows / pageSize);
          elements.push(rightestPageId);
        }
      }
      if (maxLoopIterations !== pageCount) {
        const hasPagesBefore = leftestPageId > 0;
        const hasPagesAfter = rightestPageId < pageCount;
        if (leftestPageId === 1) {
          // console.log('Previous is first');
          elements.unshift(leftestPageId - 1);
        } else if (hasPagesBefore) {
          if (!hasPagesAfter && rightestPageId === pageCount - 1) {
            // console.log('Previous is not first but right is at the end');
            if (leftestPageId === 2) {
              elements.unshift(leftestPageId-2);
              elements.unshift(leftestPageId-1);
            } else {
              elements.unshift(leftestPageId-1);
              elements.unshift('...');
            }
          } else {
            // console.log('Previous is not first and right is not at the end');
            elements.unshift('...');
          }
        }
        if (rightestPageId === pageCount - 1) {
          // console.log('Next is last', elements.length, maxPageAmount);
          if (elements.length === maxPageAmount) {
            elements[elements.length-1] = "...";
          } else {
            elements.push(rightestPageId + 1);
          }
        } else if (hasPagesAfter) {
          if (leftestPageId !== 1 && !hasPagesBefore) {
            // console.log('Next is not last and had no left');
            if (rightestPageId === pageCount - 2) {
              // console.log("Thankfully it was the almost left!");
              elements.push(rightestPageId+1);
              elements.push(rightestPageId+2);
            } else {
              // console.log("Sadly it couldn't finish");
              elements.push(rightestPageId+1);
              elements.push('...');
            }
          } else {
            // console.log('Next is not last');
            elements.push('...');
          }
        }
      }
    }

    if (pageCount <= maxPageAmount) {
      if (elements.length === pageCount + 1) {
        if (elements[elements.length-1] === "...") {
          elements.pop();
          elements[elements.length-1] = "...";
        } else {
          elements.pop();
        }
      }
    } else {
      if (elements[elements.length-1] === pageCount && elements[0] === "..." && typeof elements[1] === "number") {
        elements.pop();
        elements[0] = (elements[1] as number)-1;
        elements.unshift("...");
        if (elements.length < maxPageAmount) {
          elements[0] = (elements[1] as number)-1;
          elements.unshift("...");
        }
      }
      if (elements[elements.length-1] === "..." && elements[elements.length-2] === pageCount-2) {
        elements[elements.length-1] = pageCount-1;
      }
    }

    return {
      realCurrentPageId: realPageId,
      pageCount,
      elements
    };
  }

  private updatePagination() {
    this.pageCount = Math.ceil(this.filteredRows.length / this.pageSize);
    if (this.maxPaginationButtons === 0 || this.pageCount <= 1) {
      this.paginationRowRange = [0, this.pageSize];
      this.paginationElements = [];
      this.updatePaginatedRange();
      return;
    }
    const currentPageId = Math.max(0, typeof this.currentPageId !== 'number' ? this.page-1 : this.currentPageId);
    const rows = this.filteredRows.length;
    const pageSize = this.pageSize;
    const maxPageAmount = this.maxPaginationButtons;

    const desc = this.getPaginationDescriptor(currentPageId, rows, pageSize, maxPageAmount);
    // console.log({currentPageId, rows, pageSize, maxPageAmount});
    // console.log(desc);
    this.pageCount = desc.pageCount;
    this.currentPageId = desc.realCurrentPageId;
    this.paginationRowRange = [this.currentPageId * this.pageSize, (this.currentPageId + 1) * this.pageSize];
    const paginationElements: PaginationElement[] = [];
    for (let i = 0; i < desc.elements.length; i++) {
      let actionId: number | string;
      if (desc.elements[i] === '...') {
        if (i === 0) {
          actionId = typeof desc.elements[i+1] === 'number' ? ((desc.elements[i+1] as number) - 1) : 'none';
        } else {
          actionId = typeof desc.elements[i-1] === 'number' ? ((desc.elements[i-1] as number) + 1) : 'none';
        }
      } else {
        actionId = desc.elements[i];
      }
      paginationElements.push({
        action: (typeof desc.elements[i] !== 'string' && actionId === this.currentPageId) ? 'active' : actionId,
        enabled: typeof desc.elements[i] === 'string' ? true : (actionId !== this.currentPageId),
        content: {
          type: 'label',
          label: typeof desc.elements[i] === 'string' ? desc.elements[i].toString() : (typeof actionId === 'number' ? (actionId + 1).toString() : actionId)
        }
      });
    }
    this.paginationElements = paginationElements;
    this.updatePaginatedRange();
    /*
    {
      const currentPageId = 0;
      const rows = 4;
      const pageSize = 3;
      const maxPageAmount = 5;

      console.log('-------------');
      const desc = this.getPaginationDescriptor(currentPageId, rows, pageSize, maxPageAmount);
      console.log({currentPageId, rows, pageSize, maxPageAmount});
      console.log(desc);
      console.log(desc.elements);
    }
    */
  }

  getValueFromRowAndPropertyArray(row: any, properties: string | string[]) {
    if (!row || !properties) {
      return null;
    }
    if (typeof properties === 'string') {
      properties = [properties];
    }
    let propertyIndex = 0;
    let nodeKey = properties[propertyIndex];
    let node = row[nodeKey];
    if (typeof node === 'undefined' || node === null) {
      console.warn(`Node "${nodeKey}" from index ${propertyIndex} not found in ${JSON.stringify(row)}`);
      return null;
    }
    while (typeof node === 'object' && typeof nodeKey === 'string') {
      propertyIndex++;
      if (propertyIndex === properties.length) {
        break;
      }
      nodeKey = properties[propertyIndex];
      if (typeof nodeKey !== 'string') {
        throw new Error(`Expected 'string' at filter property, got ${typeof nodeKey}`);
      }
      node = node[nodeKey];
    }
    if (propertyIndex+1 === properties.length && typeof node === 'string' || typeof node === 'number') {
      return node;
    }
    return null;
  }

  getHeaderWidth(config: ColumnConfig, fallback: string) {
    if (typeof config.width === 'number') {
      return config.width + 'px';
    }
    return config.width || fallback;
  }

  updatePaginatedRange() {
    this.visibleRows = this.filteredRows.slice(this.paginationRowRange[0], this.paginationRowRange[1]);
    while (this.visibleRows.length < this.pageSize) {
      this.visibleRows.push(null);
    }
  }
}
